// The AI 助手's answers are Markdown. This draws the part of it a model
// writes — paragraphs, headings, lists, quotes, code, tables, bold,
// italics, links — and nothing else. Every character is escaped first and
// only then given its formatting, so no HTML in an answer is ever HTML,
// and a link is only ever http(s). A half-written answer (still
// streaming) draws as far as it goes: an open code fence is code to the
// end.

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const LINK = (href: string, text: string) => `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`

// Inline formatting of one escaped line: code spans kept as they are,
// links swapped out while the rest is formatted, so a URL is linked once.
function inline(line: string): string {
  return line
    .split(/(`[^`]+`)/)
    .map((part) => {
      if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) return `<code>${part.slice(1, -1)}</code>`
      const links: string[] = []
      const hold = (html: string) => `\u0000${links.push(html) - 1}\u0000`
      let s = part.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_, text: string, href: string) => hold(LINK(href, text)))
      // A bare URL: URL characters only (text after it in Chinese is not
      // part of it), a closing stop or bracket left outside.
      s = s.replace(/https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+/g, (url) => {
        const tail = url.match(/(?:[.,;:!?)]|&quot;|&gt;)+$/)?.[0] ?? ''
        const href = url.slice(0, url.length - tail.length)
        return hold(LINK(href, href)) + tail
      })
      s = s
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/__([^_]+)__/g, '<strong>$1</strong>')
        .replace(/(^|[^*\w])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>')
        .replace(/~~([^~]+)~~/g, '<del>$1</del>')
      return s.replace(/\u0000(\d+)\u0000/g, (_, i: string) => links[Number(i)])
    })
    .join('')
}

const LIST = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/
const TABLE_RULE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/

function cells(row: string): string[] {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => inline(c.trim()))
}

// A run of list lines as nested lists, by indent.
function list(lines: string[]): string {
  type Item = { indent: number; ordered: boolean; text: string }
  const items: Item[] = lines.map((l) => {
    const m = l.match(LIST)
    if (!m) return { indent: 99, ordered: false, text: inline(l.trim()) }
    return { indent: m[1].replace(/\t/g, '  ').length, ordered: /\d/.test(m[2]), text: inline(m[3]) }
  })
  let i = 0
  const build = (indent: number): string => {
    const ordered = items[i]?.ordered ?? false
    let html = ordered ? '<ol>' : '<ul>'
    while (i < items.length && items[i].indent >= indent) {
      const it = items[i]
      if (it.indent > indent && it.indent !== 99) {
        html = html.replace(/<\/li>$/, '') + build(it.indent) + '</li>'
        continue
      }
      i++
      let body = it.text
      // Lines that are not items carry on the one before.
      while (i < items.length && items[i].indent === 99) body += '<br>' + items[i++].text
      html += `<li>${body}</li>`
    }
    return html + (ordered ? '</ol>' : '</ul>')
  }
  let out = ''
  while (i < items.length) out += build(items[i].indent === 99 ? 0 : items[i].indent)
  return out
}

export function renderMarkdown(src: string): string {
  const lines = esc(src.replace(/\r\n?/g, '\n')).split('\n')
  const out: string[] = []
  let para: string[] = []
  const flush = () => {
    if (para.length) out.push(`<p>${para.map(inline).join('<br>')}</p>`)
    para = []
  }
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const fence = line.match(/^\s*```\s*([\w+-]*)\s*$/)
    if (fence) {
      flush()
      const code: string[] = []
      while (++i < lines.length && !/^\s*```\s*$/.test(lines[i])) code.push(lines[i])
      out.push(`<pre><code>${code.join('\n')}</code></pre>`)
      continue
    }
    if (!line.trim()) {
      flush()
      continue
    }
    const h = line.match(/^(#{1,6})\s+(.*)$/)
    if (h) {
      flush()
      const level = Math.min(6, h[1].length + 2)
      out.push(`<h${level}>${inline(h[2])}</h${level}>`)
      continue
    }
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      flush()
      out.push('<hr>')
      continue
    }
    if (/^&gt;\s?/.test(line)) {
      flush()
      const quote: string[] = []
      while (i < lines.length && /^&gt;\s?/.test(lines[i])) quote.push(lines[i++].replace(/^&gt;\s?/, ''))
      i--
      out.push(`<blockquote>${renderMarkdownEscaped(quote)}</blockquote>`)
      continue
    }
    if (line.includes('|') && i + 1 < lines.length && TABLE_RULE.test(lines[i + 1])) {
      flush()
      const head = cells(line)
      const rows: string[][] = []
      i += 2
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) rows.push(cells(lines[i++]))
      i--
      out.push(
        `<div class="md-table"><table><thead><tr>${head.map((c) => `<th>${c}</th>`).join('')}</tr></thead><tbody>` +
          rows.map((r) => `<tr>${head.map((_, j) => `<td>${r[j] ?? ''}</td>`).join('')}</tr>`).join('') +
          '</tbody></table></div>',
      )
      continue
    }
    if (LIST.test(line)) {
      flush()
      const run: string[] = []
      while (i < lines.length && (LIST.test(lines[i]) || (run.length && /^\s{2,}\S/.test(lines[i])))) run.push(lines[i++])
      i--
      out.push(list(run))
      continue
    }
    para.push(line)
  }
  flush()
  return out.join('')
}

// A quote's lines, already escaped: paragraphs and line breaks only.
function renderMarkdownEscaped(lines: string[]): string {
  return lines
    .join('\n')
    .split(/\n{2,}/)
    .map((p) => `<p>${p.split('\n').map(inline).join('<br>')}</p>`)
    .join('')
}
